/**
 * Utilities to resolve color placeholders to hex and vice versa
 */

import { isHandlebarsKey, normalizeBrandboardsKey, stripHandlebars, toUpperHex } from "@/util/color";

/**
 * Returns helpers for resolving color values using store.customColors
 */
export const useColorResolver = <StoreType extends { customColors?: Array<unknown> }>(
  store: StoreType
) => {
  const isCustomColorEntry = (
    val: unknown
  ): val is { custom?: string; value?: string; value8?: string } => {
    return !!val && typeof val === "object" && !Array.isArray(val);
  };

  /**
   * Resolve a handlebars key to a hex color (with '#') if found in store.customColors
   */
  const resolveHexFromKey = (key?: string): string | undefined => {
    if (!key) {
      return undefined;
    }
    const normalizedKey = normalizeBrandboardsKey(key);
    if (!normalizedKey) {
      return undefined;
    }
    const normalizedCore = stripHandlebars(normalizedKey);
    const match = store?.customColors?.find((c) => {
      if (!isCustomColorEntry(c)) {
        return false;
      }
      const candidate = normalizeBrandboardsKey(c.custom);
      return Boolean(candidate && stripHandlebars(candidate) === normalizedCore);
    });
    if (!isCustomColorEntry(match)) {
      return undefined;
    }
    return match.value || match.value8;
  };

  /**
   * Normalize an incoming color value to hex with '#', resolving handlebars keys via the store
   */
  const resolveHexFromProp = (val?: string): string => {
    if (!val) {
      return "#FFFFFF";
    }
    if (isHandlebarsKey(val) || normalizeBrandboardsKey(val)) {
      const resolved = resolveHexFromKey(val);
      return resolved ? resolved : "#FFFFFF";
    }
    const normalized = val.startsWith("#") ? val : `#${val}`;
    return normalized;
  };

  /**
   * Convert a value to 6/8 char hex string without '#', for display
   */
  const toDisplayHex = (val?: string): string => resolveHexFromProp(val).replace(/^#/, "");

  /**
   * Resolve a color prop to an uppercase hex string without '#'.
   * Combines resolveHexFromProp + toUpperHex for CSS string interpolation.
   */
  const normalizeHex = (val?: string): string => toUpperHex(resolveHexFromProp(val));

  return { isHandlebarsKey, resolveHexFromKey, resolveHexFromProp, toDisplayHex, normalizeHex } as const;
};
